import { autorun, runInAction } from "mobx";

import { naturalSort } from "@synthesis/shared-utils";

import type { FileMetaDto } from "../../api";
import { DirectoryStore } from "./directory-store";
import { FileStore } from "./file-store";
import { GeneralStore } from "./general-store";

type SortCompareFn = (a: FileMetaDto, b: FileMetaDto) => number;

function baseSortFn(
  baseNameA: string,
  baseNameB: string,
  extA = "",
  extB = "",
  direction: "ASC" | "DESC" = "ASC",
  priority: "name" | "ext" = "name",
): number {
  let firstComp: number;
  let secondComp: number;

  if (priority === "ext") {
    firstComp = naturalSort(extA, extB);
    secondComp = naturalSort(baseNameA, baseNameB);
  } else {
    firstComp = naturalSort(baseNameA, baseNameB);
    secondComp = naturalSort(extA, extB);
  }

  const result = firstComp !== 0 ? firstComp : secondComp;

  return direction === "DESC" ? -result : result;
}

export const SORTS = {
  ids: ["file.ASC", "file.DESC", "ext.ASC", "ext.DESC"],
  entities: {
    "file.ASC": {
      name: "Наименование (А-я)",
      sortFn: ((a, b) => baseSortFn(a.baseName, b.baseName, a.ext ?? "", b.ext ?? "", "ASC", "name")) satisfies SortCompareFn,
    },
    "file.DESC": {
      name: "Наименование (Я-а)",
      sortFn: ((a, b) => baseSortFn(a.baseName, b.baseName, a.ext ?? "", b.ext ?? "", "DESC", "name")) satisfies SortCompareFn,
    },
    "ext.ASC": {
      name: "Тип файла (А-я)",
      sortFn: ((a, b) => baseSortFn(a.baseName, b.baseName, a.ext ?? "", b.ext ?? "", "ASC", "ext")) satisfies SortCompareFn,
    },
    "ext.DESC": {
      name: "Тип файла (Я-а)",
      sortFn: ((a, b) => baseSortFn(a.baseName, b.baseName, a.ext ?? "", b.ext ?? "", "DESC", "ext")) satisfies SortCompareFn,
    },
    "author.ASC": {
      name: "Автор (А-я)",
      sortFn: ((a, b) => naturalSort(a.author || "", b.author || "")) satisfies SortCompareFn,
    },
    "author.DESC": {
      name: "Автор (Я-а)",
      sortFn: ((a, b) => -naturalSort(a.author || "", b.author || "")) satisfies SortCompareFn,
    },
    "date.ASC": {
      name: "Дата (сначала старые)",
      sortFn: ((a, b) => Number(a.modifiedAt) - Number(b.modifiedAt)) satisfies SortCompareFn,
    },
    "date.DESC": {
      name: "Дата (сначала новые)",
      sortFn: ((a, b) => Number(b.modifiedAt) - Number(a.modifiedAt)) satisfies SortCompareFn,
    },
  },
};

export type SortType = "file.ASC" | "file.DESC" | "ext.ASC" | "ext.DESC" | "author.ASC" | "author.DESC" | "date.ASC" | "date.DESC";

export type Content = {
  id: string;
  baseName: string;
  ext: string;
};

export const StoreState = {
  Initial: "init",
  Success: "success",
  Loading: "loading",
  BlockerLoading: "blocker",
  Error: "error",
} as const;

export const ExplorerView = {
  Grid: "Grid",
  List: "List",
  Table: "Table",
  Folder: "Folder",
} as const;

export type StoreStateValue = (typeof StoreState)[keyof typeof StoreState];
export type ExplorerViewValue = (typeof ExplorerView)[keyof typeof ExplorerView];

export type FileSystemTab = "models" | "docs" | "drawings";
export type FileSystemViewTab = "models" | "docs";

const EXPLORER_VIEW_STORAGE_KEY = "explorerViewByTab";
const LEGACY_EXPLORER_VIEW_STORAGE_KEY = "explorerView";

const isExplorerViewValue = (value: unknown): value is ExplorerViewValue =>
  value === ExplorerView.Grid || value === ExplorerView.List || value === ExplorerView.Folder || value === ExplorerView.Table;

const readStoredViews = (): Partial<Record<FileSystemViewTab, ExplorerViewValue>> => {
  const raw = localStorage.getItem(EXPLORER_VIEW_STORAGE_KEY);

  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw);

      if (parsed && typeof parsed === "object") {
        const record = parsed as Record<string, unknown>;
        const result: Partial<Record<FileSystemViewTab, ExplorerViewValue>> = {};

        if (isExplorerViewValue(record.models)) {
          result.models = record.models;
        }

        if (isExplorerViewValue(record.docs)) {
          result.docs = record.docs;
        }

        return result;
      }
    } catch {
      // Fall through to the legacy single-view key.
    }
  }

  const legacy = localStorage.getItem(LEGACY_EXPLORER_VIEW_STORAGE_KEY);

  if (isExplorerViewValue(legacy)) {
    return { models: legacy, docs: legacy };
  }

  return {};
};

export type History = {
  id: string | null;
  parentId: string | null;
  name: string;
};

export type Checked = {
  id: string | null;
  type: string;
  size: number;
};

export class RootStore {
  generalStore: GeneralStore;
  fileStore: FileStore;
  directoryStore: DirectoryStore;

  constructor(projectId: string, onlyModels: boolean) {
    this.generalStore = new GeneralStore(null as unknown as FileStore, null as unknown as DirectoryStore, projectId);
    this.fileStore = new FileStore(onlyModels, projectId, this.generalStore, null as unknown as DirectoryStore);
    this.directoryStore = new DirectoryStore(projectId, this.generalStore);

    this.generalStore.fileStore = this.fileStore;
    this.generalStore.directoryStore = this.directoryStore;

    this.fileStore.directoryStore = this.directoryStore;
    this.generalStore.hydrateViews(readStoredViews());
    this.initReactions();
  }

  initReactions() {
    autorun(() => {
      if (this.directoryStore.currentFolder) {
        this.generalStore.getContents(this.directoryStore.currentFolder.id);
      }
    });

    autorun(() => {
      const localData = localStorage.getItem("storedFavorites");
      if (localData) {
        const data = JSON.parse(localData);

        if (Array.isArray(data)) {
          localStorage.removeItem("storedFavorites");
        }

        this.generalStore.setFavorites(data);
      }
    });

    autorun(() => {
      localStorage.setItem(
        EXPLORER_VIEW_STORAGE_KEY,
        JSON.stringify({
          models: this.generalStore.viewByTab.models,
          docs: this.generalStore.viewByTab.docs,
        }),
      );
    });

    autorun(() => {
      localStorage.setItem("storedFavorites", JSON.stringify(this.generalStore.favorites));
    });

    autorun(() => {
      if (this.generalStore.history.length >= 0) {
        runInAction(() => {
          this.generalStore.checkedFiles = new Map();
        });
      }
    });
  }
}
